export const kioskReleasesResource = {
  name: 'Kiosk releases',
  title: 'Release %{version}',
  sections: { details: 'Details', rollouts: 'Rollouts', installs: 'Kiosks on this release' },
  kinds: { app: 'Kiosk app', ocrl: 'Octopus library', launcher: 'Launcher' },
  statuses: { uploading: 'Uploading', ready: 'Ready', revoked: 'Revoked' },
  badges: {
    signatureChecked: 'Signature checked',
    packageChecked: 'Package checked',
    touchesOctopus: 'Touches Octopus',
    journalChange: 'Journal change needs confirmation',
  },
  fields: {
    version: 'Version',
    kind: 'Kind',
    build: 'Build',
    status: 'Status',
    createdAt: 'Created',
    sizeBytes: 'Size',
    gitCommit: 'Git commit',
    keyId: 'Signed with',
    environmentPolicy: 'Environments',
    minLauncherVersion: 'Needs launcher',
    journalSchema: 'Journal schema',
    ocrlVersion: 'Octopus library version',
    oclReference: 'OCL reference',
    notes: 'Notes',
    manifestFiles: 'Files',
    activeRollouts: 'Active rollouts',
    uploadError: 'Upload problem',
    revokeReason: 'Revoked because',
  },
  upload: {
    button: 'Upload release',
    title: 'Upload release',
    intro: 'Choose the package (.zip), its manifest.json and its manifest.sig.',
    tabs: { sign: 'Sign here', signed: 'Already signed' },
    unsignedManifest: 'Unsigned manifest.json from the build',
    keyFile: 'Release key file',
    password: 'Key file password',
    review: 'Check before signing',
    reviewLabels: {
      kind: 'Kind',
      version: 'Version',
      build: 'Build',
      commit: 'Git commit',
      environments: 'Environments',
      octopusAffecting: 'Touches Octopus',
      journal: 'Journal change',
      files: 'Files',
      package: 'Package',
      manifestFile: 'Manifest file',
      packageFile: 'Package file',
    },
    signAndUpload: 'Sign and upload',
    saveSigned: 'Save the signed manifest.json and manifest.sig (needed for a hand install)',
    openRelease: 'Open the release',
    manifestRefused: 'The backend would refuse this manifest (%{reason}). Fix the build, then check again.',
    signatureNote: 'The signature is checked before the upload starts.',
    package: 'Package (.zip)',
    manifest: 'manifest.json',
    signature: 'manifest.sig',
    submit: 'Upload',
    done: 'Release ready',
    again: 'Upload the package again',
    releaseGone: 'This upload is no longer open. It was removed, revoked or already finished. Choose the files and upload again to start a new one.',
    mismatchMissing: 'The package is not in storage. Upload the package again.',
    mismatchReason: 'The uploaded package does not match its manifest (%{reason}). Upload the package again.',
    benchBuild: 'This package is a bench build (%{entry} contains %{marker}), and bench builds are only for the bench kiosk. First open this upload in the release list and press Delete. Then build the release again without the bench option and upload the new package.',
    fileErrors: {
      manifest_empty: 'The manifest file is empty.',
      signature_format: 'The signature file must hold exactly 88 characters of base64 and nothing else.',
      package_not_zip: 'The package must be a .zip file.',
      upload_failed: 'The package upload did not finish. Try again.',
      upload_failed_status: 'The package upload did not finish (Storage answered %{status}). Try again.',
    },
  },
  rollout: {
    new: 'New rollout',
    title: 'New rollout',
    who: 'Who gets it',
    targets: { all: 'All kiosks', site: 'A site', kiosk: 'Chosen kiosks', group: 'A group' },
    sites: 'Site',
    kiosks: 'Kiosks',
    groups: 'Groups',
    targetGroup: 'Group: %{name}',
    channel: 'Channel',
    channels: { production: 'Production', pilot: 'Pilot' },
    pace: 'Pace',
    policies: { one_at_a_time_per_site: 'Kiosks per site at a time', all_at_once: 'All at once' },
    siteConcurrency: 'Kiosks per site at a time',
    siteConcurrencyHelp: 'At most this many kiosks at each site take the release at once. Use 1 until one kiosk has run it for 24 hours.',
    paceShort: '%{count} per site',
    notBefore: 'Not before',
    urgent: 'Urgent',
    urgentReason: 'Why is this urgent?',
    fixedLine: 'Kiosks install only when nobody is using them and no payment is in progress, inside each site\'s night window (02:00 to 05:00 unless the site changed it). Urgent skips only the night window.',
    journalWarning: 'This release changes the kiosk\'s payment records in a way older versions cannot read. A rollback during its first minutes is still safe, but there is no way back after that.',
    journalConfirm: 'I understand',
    submit: 'Start rollout',
    created: 'Rollout started',
  },
  rolloutStatuses: { active: 'Active', paused: 'Paused', cancelled: 'Cancelled', completed: 'Completed' },
  pauseReasons: {
    rolled_back: 'A kiosk rolled back',
    crash_loop: 'A kiosk keeps restarting',
    failed: 'A kiosk could not install it',
    manual: 'Paused by %{name}',
  },
  actions: {
    pause: 'Pause',
    resume: 'Resume',
    cancel: 'Cancel rollout',
    revoke: 'Revoke',
    delete: 'Delete',
    save: 'Save',
    pauseReason: 'Why pause?',
    revokeReason: 'Why revoke?',
    revokeNote: 'Kiosks already on this release keep it. Its rollouts stop.',
    cancelNote: 'Kiosks that have not started installing stop; kiosks already on it keep it.',
    deleteNote: 'Deletes this unfinished upload.',
    done: 'Saved',
  },
  installStates: {
    pending: 'Offered',
    downloading: 'Downloading',
    downloaded: 'Downloaded',
    waiting_for_idle: 'Waiting',
    installing: 'Installing',
    probation: 'Checking the new version',
    installed: 'Installed',
    failed: 'Failed',
    rolled_back: 'Rolled back',
    cancelled: 'Withdrawn',
  },
  waitingReasons: {
    not_before: 'Not before %{time}',
    busy: 'Kiosk in use',
    settlement: 'Octopus settlement first',
    money: 'Payment in progress',
    offline: 'Kiosk offline',
    window: 'Outside the night window',
    reader: 'Octopus reader reconnecting',
    disk_space: 'Not enough disk space',
    settings_invalid: 'Waiting: the update window or idle settings for this site are invalid. Fix them in kiosk settings.',
  },
  blocked: { launcher_outdated: 'Needs a newer launcher' },
  updateErrors: {
    signature_invalid: 'Signature check failed',
    key_unknown: 'Signed with a key the kiosk does not trust',
    manifest_invalid: 'Release manifest not valid',
    package_invalid: 'Release package not valid',
    environment_not_allowed: 'Not signed for this kiosk\'s environment',
    bench_build: 'Bench build refused on a production kiosk',
    downgrade: 'Older than the installed version',
    launcher_too_old: 'Needs a newer launcher',
    hash_mismatch: 'Package does not match its manifest',
    size_mismatch: 'Package size does not match its manifest',
    disk_full: 'Not enough disk space',
    extract_failed: 'Could not unpack the package',
    file_hash_mismatch: 'An unpacked file does not match its manifest',
    stray_ocrl_file: 'Package carries an Octopus library file',
    self_test_failed: 'New launcher failed its self-test',
    power_or_stop: 'Stopped by a power cut or a restart',
    probation_crash: 'New version crashed while being checked',
    probation_timeout: 'New version was not ready in time',
    probation_hung: 'New version stopped responding',
    check_database: 'New version could not open the payment records',
    check_settings: 'New version could not load its settings',
    check_first_frame: 'New version did not show its screen',
    check_reader: 'New version could not reach the Octopus reader',
    check_ocrl_version: 'Octopus library version check failed',
    heartbeat_rejected: 'The server refused the new version',
    launcher_probation_failed: 'New launcher failed its check',
    launcher_reverted: 'Launcher went back to the previous version',
    integrity_failed: 'Installed files failed the integrity check',
    crash_loop: 'Kiosk app keeps crashing',
  },
  progress: '%{installed} of %{targeted} installed',
  files: 'Files for a hand install',
  filesPackage: 'Download %{file}',
  keyRetired: 'Signed with a retired key: release it again under a new version',
  columns: {
    target: 'Target',
    channel: 'Channel',
    policy: 'Pace',
    key: 'Key',
    status: 'Status',
    progress: 'Progress',
    kiosk: 'Kiosk',
    site: 'Site',
    state: 'State',
    attempts: 'Attempts',
    lastError: 'Last error',
    updatedAt: 'Updated',
    lastHeartbeatAt: 'Last heartbeat',
  },
  errors: {
    KIOSK_RELEASE_NOT_FOUND: 'Kiosk release not found',
    KIOSK_RELEASE_NOT_OFFERED: 'This release is not offered to this kiosk',
    KIOSK_RELEASE_MANIFEST_INVALID: 'The release manifest is not valid',
    KIOSK_RELEASE_SIGNATURE_INVALID: 'The release signature does not match a known release key',
    KIOSK_RELEASE_ENVIRONMENT_NOT_ALLOWED: 'This release is not signed for this environment',
    KIOSK_RELEASE_VERSION_EXISTS: 'A release of this kind and version already exists',
    KIOSK_RELEASE_PACKAGE_MISMATCH: 'The uploaded package does not match its manifest',
    KIOSK_RELEASE_NOT_READY: 'This release is not ready to roll out',
    KIOSK_RELEASE_OCL_REFERENCE_REQUIRED: 'Add the OCL reference before rolling this release out to production kiosks',
    KIOSK_RELEASE_JOURNAL_CONFIRMATION_REQUIRED: 'Confirm the journal change before rolling this release out',
    KIOSK_ROLLOUT_NOT_FOUND: 'Rollout not found',
    KIOSK_ROLLOUT_STATE_CONFLICT: 'This rollout cannot be changed in its current state',
    KIOSK_ROLLOUT_RING_NOT_MET: 'Install this release on the test group, then on one kiosk for 24 hours, or mark the rollout urgent',
    KIOSK_ROLLOUT_OVERLAP: 'Another active or paused rollout of this kind already covers some of these kiosks; cancel it first',
    KIOSK_ROLLOUT_ALL_AT_ONCE_NOT_ALLOWED: 'Only a rollout to the test group may go to every kiosk at once; choose how many kiosks per site update at a time',
    KIOSK_GROUP_NOT_FOUND: 'Kiosk group not found',
    KIOSK_GROUP_NAME_EXISTS: 'A kiosk group with this name already exists',
    KIOSK_GROUP_EMPTY: 'This kiosk group has no active kiosks',
    KIOSK_GROUP_IN_USE: 'This kiosk group cannot be deleted now',
    KIOSK_UPDATE_RETRY_REFUSED: 'This update cannot be retried now.',
    KIOSK_RELEASE_KEY_NOT_SET: 'Create the release key first',
    KIOSK_RELEASE_KEY_EXISTS: 'A release key already exists; rotate it instead',
    KIOSK_RELEASE_KEY_INVALID: 'The release key, its proof or its rotation statement is not valid',
    KIOSK_RELEASE_KEY_RETIRED: 'This release is signed with a key that is no longer active; release it again under a new version',
    KIOSK_RELEASE_KEY_IN_USE: 'Open rollouts use releases signed with the current key; cancel them first',
    KIOSK_RELEASE_KEY_STALE: 'The release key changed since this page was opened; reload it and try again',
    KIOSK_RELEASE_BENCH_BUILD: 'This package is a bench build, which production refuses',
    KIOSK_NOT_FOUND: 'Kiosk not found',
    SITE_NOT_FOUND: 'Site not found',
    PERMISSION_DENIED: 'Permission denied',
    VALIDATION_FAILED: 'Check the form',
    generic: 'Something went wrong. Try again.',
    byReason: {
      KIOSK_ROLLOUT_RING_NOT_MET: {
        rolled_back: 'This release was rolled back on a kiosk. Upload a newer release to roll out again.',
        test_group_first: 'Install this release on the test group first, or mark the rollout urgent.',
        one_kiosk_24h_first: 'Install this release on one kiosk for 24 hours first, or mark the rollout urgent.',
        test_group_changed: 'The test group changed while this rollout was being created. Check the targets and try again.',
      },
      KIOSK_RELEASE_OCL_REFERENCE_REQUIRED: {
        active_rollout: 'A rollout of this release is active, so its OCL reference cannot be cleared.',
      },
      KIOSK_RELEASE_SIGNATURE_INVALID: {
        test_key_on_production: 'The active release key is a test key, which production refuses. Replace it with a real key before releasing.',
        key_retired: 'This manifest is signed with a retired release key. Sign it again with the active key.',
        key_unknown: 'This manifest is signed with a key this deployment does not know. Sign it with the active key.',
        signature_malformed: 'The signature is not a valid release signature. Sign the manifest again.',
        signature_invalid: 'The signature does not match this manifest and the active key. Sign the manifest again.',
      },
      KIOSK_RELEASE_KEY_INVALID: {
        public_key_malformed: 'The public key is not a valid Ed25519 public key.',
        public_key_unusable: 'This public key cannot be used for signing. Make a new key.',
        test_key_on_production: 'This is a test release key; production refuses it. Make a new key.',
        proof_invalid: 'The key file did not prove that it holds this key. Open the key file again and retry.',
        statement_malformed: 'The rotation statement is not valid. Start the rotation again.',
        statement_stale: 'The rotation statement is too old or dated in the future. Check this computer\'s clock and start the rotation again.',
        statement_not_from_active_key: 'The release key changed since this page was opened. Reload the page and start again.',
        signature_invalid: 'The current key file did not sign this rotation. Load the current key file again.',
        key_used_before: 'This key was used before. Make a new key.',
      },
      KIOSK_GROUP_IN_USE: {
        test_group: 'This is the test group. Make another group the test group, or turn off Test group here, before deleting it.',
        open_rollout: 'A rollout to this group is still open. Cancel it or let it finish before deleting the group.',
      },
    },
  },
};

export const kioskReleases = kioskReleasesResource;
